/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { X, Sparkles, Dumbbell, Trash2, Heart, Zap, Wand2 } from "lucide-react";
import { useCountdown } from "@/lib/time";

const IV_LABELS = ["PV", "Atk", "Déf", "Atk Spé", "Déf Spé", "Vit"];

// Cache pokedex descriptions in memory (never changes per session)
const DESC_CACHE = new Map();

async function fetchPokedexDesc(pokemonId) {
  if (DESC_CACHE.has(pokemonId)) return DESC_CACHE.get(pokemonId);
  try {
    const res = await fetch(`https://pokeapi.co/api/v2/pokemon-species/${pokemonId}`);
    if (!res.ok) throw new Error("PokeAPI");
    const data = await res.json();
    // Prefer French entry, fall back to English
    const frEntry = data.flavor_text_entries?.find((e) => e.language?.name === "fr");
    const enEntry = data.flavor_text_entries?.find((e) => e.language?.name === "en");
    const raw = (frEntry || enEntry)?.flavor_text || "";
    const cleaned = raw.replace(/[\f\n\r\u000c]/g, " ").trim();
    DESC_CACHE.set(pokemonId, cleaned);
    return cleaned;
  } catch {
    DESC_CACHE.set(pokemonId, "");
    return "";
  }
}

function shinySprite(pid, isShiny) {
  // Use official artwork for the detail modal (higher resolution than pixel sprites)
  return isShiny
    ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/shiny/${pid}.png`
    : `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${pid}.png`;
}

function GenderIcon({ gender }) {
  if (gender === "M") return <span className="text-[#2E5EAA] font-bold" title="Mâle">♂</span>;
  if (gender === "F") return <span className="text-[#B8336A] font-bold" title="Femelle">♀</span>;
  return <span className="text-[#7A7A7A]" title="Sans genre">—</span>;
}

/**
 * Detail modal for a single Pokémon (from PC or trainer team).
 * Props:
 *  - poke: pokemon data { pokemon_id, name, level, xp, iv_stats, iv, is_shiny, gender, happiness, training, training_ends_at, caught_at, caught_by_trainer_name }
 *  - context: "pc" | "team"
 *  - onClose: () => void
 *  - onTrain: () => void      (PC only, if not training)
 *  - onRelease: () => void    (PC only)
 */
export default function PokemonDetailModal({ poke, context, onClose, onTrain, onRelease, availableStones = [], onEvolve }) {
  const [desc, setDesc] = useState("");
  const [descLoading, setDescLoading] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [evolvingStone, setEvolvingStone] = useState(null);
  const { label: trainingTimer, urgent: trainingUrgent } = useCountdown(poke?.training_ends_at);

  useEffect(() => {
    if (!poke) return;
    setDescLoading(true);
    fetchPokedexDesc(poke.pokemon_id).then((d) => { setDesc(d); setDescLoading(false); });
  }, [poke]);

  if (!poke) return null;

  const xpNeeded = poke.level * 30; // matches POKEMON_XP_FACTOR
  const xpPct = Math.min(100, Math.round(((poke.xp || 0) / xpNeeded) * 100));
  const happiness = poke.happiness ?? 70;
  const happinessPct = Math.min(100, Math.round((happiness / 255) * 100));
  const ivStats = poke.iv_stats || [15, 15, 15, 15, 15, 16];
  const ivTotal = ivStats.reduce((s, v) => s + v, 0);
  const ivMax = 6 * 31; // 186 (Gen 3+ canonical range 0-31 per stat)

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 overflow-auto"
      style={{ background: "rgba(26,26,26,0.6)", zIndex: 80 }}
      onClick={onClose}
      data-testid="pokemon-detail-modal"
    >
      <div
        className="panel-strong p-0 max-w-2xl w-full my-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="pokemon-detail-close"
          aria-label="Fermer la fiche du Pokémon"
          className="absolute top-2 right-2 z-20 w-11 h-11 rounded-full bg-[#1A1A1A] text-[#F4EFE6] border-2 border-[#F4EFE6] flex items-center justify-center shadow-lg hover:bg-[#8C2A2A] transition-colors"
          onClick={onClose}
        >
          <X size={22} strokeWidth={2.5}/>
        </button>

        {/* Header */}
        <header className={`p-5 border-b border-[#2B2B2B] ${poke.is_shiny ? "bg-[#FFF4D9]" : "bg-[#EAE4D9]"}`}>
          <div className="flex items-start gap-5">
            <img
              src={shinySprite(poke.pokemon_id, poke.is_shiny)}
              alt={poke.name}
              className="w-48 h-48 shrink-0 object-contain drop-shadow-md"
              style={{ imageRendering: "auto" }}
              data-testid="pokemon-detail-sprite"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[11px] text-[#7A7A7A]">#{poke.pokemon_id.toString().padStart(3, "0")}</span>
                <span className="overline">Niv {poke.level}</span>
                {poke.is_shiny && (
                  <span className="font-mono text-[10px] border border-[#B87333] bg-[#FCF9F2] text-[#B87333] px-1.5 py-0.5 inline-flex items-center gap-1">
                    <Sparkles size={10}/> Chromatique
                  </span>
                )}
              </div>
              <h2 className="font-serif-display text-4xl leading-none mt-1 flex items-center gap-2" data-testid="pokemon-detail-name">
                {poke.name}
                <GenderIcon gender={poke.gender}/>
              </h2>
              {poke.caught_by_trainer_name && (
                <div className="mt-2 text-[11px] font-mono text-[#4A4A4A]">
                  Capturé par <strong className="text-[#1A1A1A]">{poke.caught_by_trainer_name}</strong>
                  {poke.caught_at && <> · {new Date(poke.caught_at).toLocaleDateString()}</>}
                </div>
              )}
              {/* XP bar */}
              <div className="mt-3">
                <div className="flex justify-between text-[10px] font-mono text-[#4A4A4A]">
                  <span>XP</span>
                  <span>{poke.xp || 0}/{xpNeeded}</span>
                </div>
                <div className="h-[5px] bg-[#FCF9F2] border border-[#2B2B2B] mt-0.5">
                  <div className="h-full bg-[#2E4F32]" style={{ width: `${xpPct}%` }}/>
                </div>
              </div>
              {/* Happiness bar */}
              <div className="mt-2">
                <div className="flex justify-between text-[10px] font-mono text-[#4A4A4A]">
                  <span className="inline-flex items-center gap-1"><Heart size={9} className="text-[#B8336A]"/> Bonheur</span>
                  <span>{happiness}/255</span>
                </div>
                <div className="h-[5px] bg-[#FCF9F2] border border-[#2B2B2B] mt-0.5">
                  <div className="h-full bg-[#B8336A]" style={{ width: `${happinessPct}%` }}/>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Pokédex description */}
          <div data-testid="pokemon-detail-desc">
            <div className="overline mb-1">Notice du Pokédex</div>
            <p className={`text-sm italic ${desc ? "text-[#1A1A1A]" : "text-[#7A7A7A]"}`}>
              {descLoading ? "Chargement de la notice…" : (desc || "Aucune notice disponible.")}
            </p>
          </div>

          {/* IV stats */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <div className="overline">Valeurs Individuelles (IV)</div>
              <div className="font-mono text-[11px] text-[#4A4A4A]">
                Total {ivTotal}/{ivMax} <span className="text-[#B87333]">({Math.round(ivTotal / ivMax * 100)}%)</span>
              </div>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="pokemon-detail-iv">
              {ivStats.map((val, i) => {
                const pct = Math.min(100, Math.round((val / 31) * 100));
                const color = val >= 26 ? "#2E4F32" : val >= 16 ? "#B87333" : "#8C2A2A";
                return (
                  <div key={i} className="panel p-2">
                    <div className="overline text-[9px]">{IV_LABELS[i]}</div>
                    <div className="font-mono text-lg leading-none mt-0.5" style={{ color }}>{val}<span className="text-[9px] text-[#7A7A7A]">/31</span></div>
                    <div className="h-[3px] bg-[#EAE4D9] border border-[#2B2B2B] mt-1">
                      <div className="h-full" style={{ width: `${pct}%`, backgroundColor: color }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          {context === "pc" && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-[#D8D2C4]">
              {poke.training ? (
                <div className={`px-3 py-2 border border-[#B87333] text-[#B87333] font-mono text-xs ${trainingUrgent ? "timer-urgent" : ""}`} data-testid="pokemon-detail-training">
                  <Zap size={11} className="inline mr-1"/> En entraînement · {trainingTimer}
                </div>
              ) : (
                <button
                  data-testid="pokemon-detail-train"
                  className="btn-ink btn-leaf"
                  onClick={onTrain}
                >
                  <Dumbbell size={12}/> Entraîner (2 min+)
                </button>
              )}
              {!poke.training && availableStones.length > 0 && onEvolve && (
                <div className="w-full flex flex-wrap items-center gap-2 pt-2" data-testid="pokemon-detail-stones">
                  <span className="overline shrink-0">Évolution par objet</span>
                  {availableStones.map((s) => (
                    <button
                      key={s.item_id}
                      data-testid={`pokemon-detail-evolve-${s.item_id}`}
                      className="btn-ink"
                      disabled={evolvingStone === s.item_id}
                      title={`Utiliser une ${s.name} sur ${poke.name}`}
                      onClick={async () => {
                        setEvolvingStone(s.item_id);
                        try {
                          await onEvolve(s.item_id);
                        } finally {
                          setEvolvingStone(null);
                        }
                      }}
                    >
                      <Wand2 size={11}/> {s.name} ({s.qty})
                    </button>
                  ))}
                </div>
              )}
              {!poke.training && (
                confirmRelease ? (
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="text-xs text-[#8C2A2A] font-mono">Définitif ?</span>
                    <button
                      data-testid="pokemon-detail-release-confirm"
                      className="btn-ink btn-accent"
                      onClick={() => { setConfirmRelease(false); onRelease?.(); }}
                    >
                      Oui, relâcher
                    </button>
                    <button
                      data-testid="pokemon-detail-release-cancel"
                      className="btn-ink"
                      onClick={() => setConfirmRelease(false)}
                    >
                      Annuler
                    </button>
                  </div>
                ) : (
                  <button
                    data-testid="pokemon-detail-release"
                    className="btn-ink btn-accent ml-auto"
                    onClick={() => setConfirmRelease(true)}
                    title="Action irréversible"
                  >
                    <Trash2 size={12}/> Relâcher
                  </button>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
